import { z } from "zod";

import { ToolResultSchema } from "../domain/chat/content-part.types";

export const ASK_USER_QUESTION_TOOL_NAME = "ask_user_question" as const;

/**
 * Tool-result string written when the user DECLINES an AskUserQuestion (closes / Esc) instead
 * of answering it. Mirrors Claude Code's own decline copy. Unlike answer/Skip, decline has no
 * continuation turn — the backend persists this verbatim into the rejected TOOL row, and the FE
 * writes the byte-identical string into the local tool-result so the live transcript settles to
 * the same "Declined…" render a reload produces. Single source so the two never drift.
 */
export const QUESTION_HOLD_DECLINED_TOOL_RESULT = "User declined to answer questions";

/**
 * Tool-result value written for a single question the user SKIPPED (the per-question
 * Skip button) inside an otherwise-answered AskUserQuestion. The agent reads explicit
 * text instead of a bare value-less answer. Distinct from decline
 * (QUESTION_HOLD_DECLINED_TOOL_RESULT), which rejects the whole tool call. Single source so the
 * emit path and the schema doc never drift.
 */
export const QUESTION_HOLD_SKIPPED_ANSWER = "The user skipped answering the question";

// =============================================================================
// Zod schemas (source of truth — both backend and frontend consume these)
// =============================================================================

export const AskUserQuestionOptionSchema = z.object({
  label: z.string().min(1).max(200),
  description: z.string().min(1).max(300).optional()
});

export const AskUserQuestionQuestionSchema = z.object({
  id: z.string().uuid(),
  question: z.string().min(1).max(500),
  type: z.enum(["single_choice", "multiple_choice"]),
  header: z.string().min(1).max(12).optional(),
  options: z.array(AskUserQuestionOptionSchema).min(2).max(4)
});

export const AskUserQuestionInputSchema = z
  .object({
    questions: z.array(AskUserQuestionQuestionSchema).min(1).max(4)
  })
  .refine(
    (val) => {
      const ids = val.questions.map((q) => q.id);
      return new Set(ids).size === ids.length;
    },
    { message: "question ids must be unique within a single tool call", path: ["questions"] }
  )
  .refine(
    (val) => {
      const texts = val.questions.map((q) => q.question);
      return new Set(texts).size === texts.length;
    },
    { message: "question texts must be unique within a single tool call", path: ["questions"] }
  );

export const AskUserQuestionAnswerSchema = z.discriminatedUnion("type", [
  z.object({
    type: z.literal("single_choice"),
    questionId: z.string(),
    selected: z.string() // "" = skipped
  }),
  z.object({
    type: z.literal("multiple_choice"),
    questionId: z.string(),
    selected: z.array(z.string()) // [] = skipped
  })
]);

export const AskUserQuestionResultSchema = z.object({
  answers: z.array(AskUserQuestionAnswerSchema)
});

// =============================================================================
// Tool-result envelope — the JSON an answered AskUserQuestion returns to the
// agent. `answers` is keyed by verbatim question text; each value is the user's
// chosen answer, or QUESTION_HOLD_SKIPPED_ANSWER for a question they skipped (empty
// selection). A decline (the user closes the panel) is a separate server-written
// `rejected` tool result carrying QUESTION_HOLD_DECLINED_TOOL_RESULT, not this envelope.
// =============================================================================

export const AskUserQuestionResponseSchema = z.object({
  outcome: z.literal("answered"),
  answers: z.record(z.string(), z.string())
});

// =============================================================================
// Inferred TypeScript types
// =============================================================================

export type AskUserQuestionOption = z.infer<typeof AskUserQuestionOptionSchema>;
export type AskUserQuestionQuestion = z.infer<typeof AskUserQuestionQuestionSchema>;
export type AskUserQuestionInput = z.infer<typeof AskUserQuestionInputSchema>;
export type AskUserQuestionAnswer = z.infer<typeof AskUserQuestionAnswerSchema>;
export type AskUserQuestionResult = z.infer<typeof AskUserQuestionResultSchema>;
export type AskUserQuestionResponse = z.infer<typeof AskUserQuestionResponseSchema>;

// =============================================================================
// Decline endpoint wire contract — the JSON shape returned by
// POST conversations/:id/decline-question. Both sides import this schema so the
// FE can validate the response without hand-rolling its own union.
// =============================================================================

export const DeclineOutcomeSchema = z.discriminatedUnion("outcome", [
  z.object({ outcome: z.literal("declined") }),
  z.object({ outcome: z.literal("skipped-duplicate"), result: ToolResultSchema })
]);

export type DeclineOutcome = z.infer<typeof DeclineOutcomeSchema>;
